<template>
  <div class="caissier-manager">
    <!-- Header Premium -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-info">
          <div class="icon-wrapper">
            <i class="fas fa-cash-register"></i>
          </div>
          <div class="header-text">
            <h2>💰 Espace Caissier</h2>
            <p>Gestion des encaissements et paiements clients</p>
          </div>
        </div>
        <div class="header-stats">
          <div class="stat-card total-card">
            <div class="stat-value">{{ formatCurrency(totalEncaissementsPeriode) }}</div>
            <div class="stat-label">Total Encaissé</div>
            <select v-model="periodeTotale" class="periode-select">
              <option value="jour">Aujourd'hui</option>
              <option value="semaine">Cette semaine</option>
              <option value="mois">Ce mois</option>
              <option value="annee">Cette année</option>
              <option value="tout">Tout</option>
            </select>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ transactionsPeriode }}</div>
            <div class="stat-label">Transactions</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Barre d'actions Financier -->
    <div v-if="userRole === 'Financier'" class="financier-actions-bar">
      <div class="actions-content">
        <div class="actions-info">
          <i class="fas fa-shield-alt"></i>
          <span>Actions Financier</span>
        </div>
        <button 
          @click="showDeclarationPerteModal = true"
          class="btn-declare-loss-pro"
        >
          <div class="btn-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="btn-text">
            <span class="btn-title">Déclarer une perte</span>
            <span class="btn-subtitle">Vol, erreur de caisse, manquant</span>
          </div>
          <i class="fas fa-chevron-right btn-arrow"></i>
        </button>
      </div>
    </div>

    <!-- Onglets -->
    <div class="tabs-section">
      <button 
        @click="activeTab = 'encaissements'"
        :class="['tab-btn', { active: activeTab === 'encaissements' }]"
      >
        <i class="fas fa-list"></i>
        {{ userRole === 'Technicien' ? 'Mes Encaissements' : 'Tous les Encaissements' }}
      </button>
      <button 
        @click="activeTab = 'depenses'"
        :class="['tab-btn', { active: activeTab === 'depenses' }]"
      >
        <i class="fas fa-receipt"></i>
        Dépenses
      </button>
      <button 
        @click="activeTab = 'controle'"
        :class="['tab-btn', { active: activeTab === 'controle' }]"
        v-if="userRole === 'Financier' || userRole === 'Admin'"
      >
        <i class="fas fa-clipboard-check"></i>
        Contrôle Financier
        <span v-if="caissesEnAttente > 0" class="badge">{{ caissesEnAttente }}</span>
      </button>

      <button 
        @click="activeTab = 'controleCreances'"
        :class="['tab-btn creances-tab', { active: activeTab === 'controleCreances' }]"
        v-if="userRole === 'Financier' || userRole === 'Admin'"
      >
        <i class="fas fa-file-invoice-dollar"></i>
        Contrôle Créances
      </button>

      <button 
        @click="activeTab = 'recouvrementCreances'"
        :class="['tab-btn recouvrement-creances-tab', { active: activeTab === 'recouvrementCreances' }]"
        v-if="userRole === 'Financier' || userRole === 'Admin'"
      >
        <i class="fas fa-money-check-alt"></i>
        Espace Recouvrement
      </button>

      <button 
        @click="activeTab = 'statistiques'"
        :class="['tab-btn', { active: activeTab === 'statistiques' }]"
        v-if="userRole !== 'Technicien'"
      >
        <i class="fas fa-chart-pie"></i>
        {{ userRole === 'Technicien' ? 'Mes Statistiques' : 'Statistiques Globales' }}
      </button>
    </div>

    <!-- Debug: Afficher l'onglet actif -->
    <div class="debug-info" style="background: #f0f0f0; padding: 10px; margin: 10px 0; border-radius: 5px;">
      <strong>Onglet actif:</strong> {{ activeTab }}
    </div>

    <!-- Contenu Encaissements -->
    <div v-if="activeTab === 'encaissements'" class="tab-content">
      <!-- Filtres -->
      <div class="filters-section">
        <div class="filter-group">
          <!-- Filtre par technicien (visible pour Admin et Financier) -->
          <div v-if="userRole === 'Admin' || userRole === 'Financier'" class="filter-wrapper filter-technicien">
            <div class="filter-icon-container">
              <i class="fas fa-user-cog"></i>
            </div>
            <select 
              v-model="filters.technicien" 
              class="filter-select"
              @change="loadEncaissements"
            >
              <option value="">Tous les techniciens</option>
              <option 
                v-for="tech in techniciens" 
                :key="tech._id" 
                :value="tech._id"
              >
                {{ tech.prenom }} {{ tech.nom }}
              </option>
            </select>
          </div>

          <div class="filter-wrapper">
            <div class="filter-icon-container">
              <i class="fas fa-calendar-alt"></i>
            </div>
            <input 
              v-model="filters.dateDebut" 
              type="date" 
              class="filter-input"
              placeholder="Date début"
            >
          </div>
          
          <div class="filter-wrapper">
            <div class="filter-icon-container">
              <i class="fas fa-calendar-check"></i>
            </div>
            <input 
              v-model="filters.dateFin" 
              type="date" 
              class="filter-input"
              placeholder="Date fin"
            >
          </div>

          <button @click="loadEncaissements" class="btn-refresh">
            <i class="fas fa-sync-alt"></i>
            Actualiser
          </button>
        </div>
      </div>

      <!-- Debug: Nombre d'encaissements -->
      <div class="debug-info" style="background: #e3f2fd; padding: 10px; margin: 10px 0; border-radius: 5px;">
        <strong>Nombre d'encaissements:</strong> {{ encaissements.length }}
        <br><strong>Loading:</strong> {{ loading }}
      </div>

      <!-- Liste des Encaissements -->
      <div class="encaissements-grid">
        <div 
          v-for="encaissement in encaissements" 
          :key="encaissement._id"
          class="encaissement-card"
        >
        <div class="card-header">
          <div class="client-info">
            <h4>{{ encaissement.rdv?.prospect?.client?.nom }} {{ encaissement.rdv?.prospect?.client?.prenom }}</h4>
            <p>{{ formatDate(encaissement.dateEncaissement) }}</p>
            <p v-if="userRole === 'Financier' || userRole === 'Admin'" class="technicien-info">
              <i class="fas fa-user-cog"></i>
              Technicien: {{ encaissement.rdv?.technicien?.prenom }} {{ encaissement.rdv?.technicien?.nom }}
            </p>
          </div>
          <div class="montant">
            {{ formatCurrency(encaissement.montant) }}
          </div>
        </div>

        <div class="card-body">
          <div class="info-row">
            <span class="label">Mode de paiement :</span>
            <span class="value">{{ getModePaiementLabel(encaissement.modePaiement) }}</span>
          </div>
          <div class="info-row">
            <span class="label">Statut :</span>
            <span class="value">
              <span class="statut-badge" :class="getStatutClass(encaissement.statutEncaissement || encaissement.encaissement?.statut)">
                {{ getStatutLabel(encaissement.statutEncaissement || encaissement.encaissement?.statut) }}
              </span>
            </span>
          </div>
          <div class="info-row">
            <span class="label">Intervention :</span>
            <span class="value">{{ formatDate(encaissement.rdv?.dateRdv) }} - {{ encaissement.rdv?.heureDebut }}</span>
          </div>
          <div v-if="encaissement.commentaires" class="info-row">
            <span class="label">Commentaires :</span>
            <span class="value">{{ encaissement.commentaires }}</span>
          </div>
        </div>

        <div class="card-actions">
          <button @click="viewDetails(encaissement)" class="btn-details">
            <i class="fas fa-eye"></i>
            Détails
          </button>
          <button v-if="encaissement.photoJustificatif" @click="viewPhoto(encaissement)" class="btn-photo">
            <i class="fas fa-image"></i>
            Photo
          </button>
        </div>
      </div>
    </div>

      <!-- État vide -->
      <div v-if="!loading && encaissements.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-receipt"></i>
        </div>
        <h3>Aucun encaissement trouvé</h3>
        <p>Les encaissements de vos interventions apparaîtront ici</p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Chargement des encaissements...</p>
      </div>
    </div>

    <!-- Contenu Dépenses -->
    <div v-if="activeTab === 'depenses'" class="tab-content">
      <DepensesManager 
        :userRole="userRole"
        :userId="userId"
      />
    </div>

    <!-- Contenu Contrôle Financier -->
    <div v-if="activeTab === 'controle'" class="tab-content">
      <div class="controle-section">
        <div class="controle-header">
          <h3>🔍 Contrôle Financier</h3>
          <p>Validation des encaissements et caisses journalières</p>
        </div>
        
        <!-- Sous-onglets -->
        <div class="sub-tabs">
          <button 
            @click="controleSubTab = 'encaissements'"
            :class="['sub-tab-btn', { active: controleSubTab === 'encaissements' }]"
          >
            <i class="fas fa-money-bill-wave"></i>
            Encaissements Soumis
            <span v-if="encaissementsSoumis.length > 0" class="badge">{{ encaissementsSoumis.length }}</span>
          </button>
          <button 
            @click="controleSubTab = 'caisses'"
            :class="['sub-tab-btn', { active: controleSubTab === 'caisses' }]"
          >
            <i class="fas fa-cash-register"></i>
            Caisses Journalières
            <span v-if="caissesEnValidation.length > 0" class="badge">{{ caissesEnValidation.length }}</span>
          </button>
          <button 
            v-if="userRole === 'Admin'"
            @click="controleSubTab = 'pertes'; loadDeclarationsPertes()"
            :class="['sub-tab-btn pertes', { active: controleSubTab === 'pertes' }]"
          >
            <i class="fas fa-exclamation-triangle"></i>
            Déclarations de Pertes
            <span v-if="declarationsPertes.length > 0" class="badge danger">{{ declarationsPertes.length }}</span>
          </button>
        </div>

        <!-- Encaissements Soumis -->
        <div v-if="controleSubTab === 'encaissements'" class="encaissements-soumis">
          <div class="encaissements-grid">
            <div 
              v-for="encaissement in encaissementsSoumis" 
              :key="encaissement._id"
              class="encaissement-validation-card"
            >
              <div class="card-header">
                <div class="client-info">
                  <h4>{{ encaissement.prospect?.client?.nomComplet || 'Client inconnu' }}</h4>
                  <p>{{ formatDate(encaissement.encaissement.dateEncaissement) }}</p>
                  <p class="technicien-info">
                    <i class="fas fa-user-cog"></i>
                    Technicien: {{ encaissement.technicien?.prenom }} {{ encaissement.technicien?.nom }}
                  </p>
                </div>
                <div class="montant">
                  {{ formatCurrency(encaissement.encaissement.montant) }}
                </div>
              </div>
              
              <div class="card-body">
                <div class="info-row">
                  <span class="label">Type de mission :</span>
                  <span class="value">
                    <span class="type-mission-badge" :class="getMissionTypeClass(encaissement.prospect?.typeIntervention)">
                      {{ encaissement.prospect?.typeIntervention || 'Non défini' }}
                    </span>
                  </span>
                </div>
                <div class="info-row">
                  <span class="label">Mode de paiement :</span>
                  <span class="value">{{ getModePaiementLabel(encaissement.encaissement.modePaiement) }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Statut :</span>
                  <span class="value">
                    <span class="statut-badge soumise">SOUMISE</span>
                  </span>
                </div>
              </div>
              
              <div class="card-actions">
                <button @click="validerEncaissement(encaissement._id, 'Validée')" class="btn-valider">
                  <i class="fas fa-check"></i>
                  Valider
                </button>
                <button @click="rejeterEncaissement(encaissement._id)" class="btn-rejeter">
                  <i class="fas fa-times"></i>
                  Rejeter
                </button>
              </div>
            </div>
          </div>
          
          <div v-if="encaissementsSoumis.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h3>Aucun encaissement en attente</h3>
            <p>Tous les encaissements sont validés</p>
          </div>
        </div>

        <!-- Caisses Journalières -->
        <div v-if="controleSubTab === 'caisses'" class="caisses-journalieres">
        <div class="caisses-grid">
          <div 
            v-for="caisse in caissesEnValidation" 
            :key="caisse._id"
            class="caisse-validation-card"
          >
            <div class="card-header">
              <div class="technicien-info">
                <h4>{{ caisse.technicien?.prenom }} {{ caisse.technicien?.nom }}</h4>
                <p>{{ formatDate(caisse.date) }}</p>
              </div>
              <div class="statut-badge" :class="getCaisseStatusClass(caisse.statut)">
                {{ caisse.statut }}
              </div>
            </div>
            
            <div class="card-body">
              <div class="montants-summary">
                <div class="montant-item">
                  <span class="label">Espèces :</span>
                  <span class="value">{{ formatCurrency(caisse.montantCashDeclare) }}</span>
                  <span v-if="caisse.ecartCash !== 0" class="ecart" :class="{ 'surplus': caisse.ecartCash > 0, 'deficit': caisse.ecartCash < 0 }">
                    ({{ caisse.ecartCash > 0 ? '+' : '' }}{{ formatCurrency(caisse.ecartCash) }})
                  </span>
                </div>
                <div class="montant-item">
                  <span class="label">Chèques :</span>
                  <span class="value">{{ formatCurrency(caisse.montantChequesDeclare) }}</span>
                  <span v-if="caisse.ecartCheques !== 0" class="ecart" :class="{ 'surplus': caisse.ecartCheques > 0, 'deficit': caisse.ecartCheques < 0 }">
                    ({{ caisse.ecartCheques > 0 ? '+' : '' }}{{ formatCurrency(caisse.ecartCheques) }})
                  </span>
                </div>
              </div>
            </div>
            
            <div class="card-actions">
              <button @click="openControleModal(caisse)" class="btn-controle">
                <i class="fas fa-clipboard-check"></i>
                Contrôler
              </button>
            </div>
          </div>
        </div>
        
        <div v-if="caissesEnValidation.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="fas fa-clipboard-check"></i>
          </div>
          <h3>Aucune caisse en attente</h3>
          <p>Toutes les caisses sont à jour</p>
          </div>
        </div>

        <!-- Déclarations de Pertes (Admin uniquement) -->
        <div v-if="controleSubTab === 'pertes' && userRole === 'Admin'" class="declarations-pertes-section">
          <div class="pertes-grid">
            <div 
              v-for="declaration in declarationsPertes" 
              :key="declaration._id"
              class="perte-validation-card"
              :class="declaration.typePerte"
            >
              <div class="card-header">
                <div class="perte-type">
                  <span class="type-badge" :class="declaration.typePerte">
                    <i :class="getPerteTypeIcon(declaration.typePerte)"></i>
                    {{ getPerteTypeLabel(declaration.typePerte) }}
                  </span>
                  <span class="date">{{ formatDate(declaration.dateDeclaration) }}</span>
                </div>
                <div class="montant-perte">
                  {{ formatCurrency(declaration.montant) }}
                </div>
              </div>
              
              <div class="card-body">
                <div class="declarant-info">
                  <i class="fas fa-user"></i>
                  <span>Déclaré par: {{ declaration.declarePar?.prenom }} {{ declaration.declarePar?.nom }}</span>
                </div>
                <div class="date-incident">
                  <i class="fas fa-calendar-alt"></i>
                  <span>Date incident: {{ formatDate(declaration.dateIncident) }}</span>
                </div>
                <div class="description">
                  <p>{{ declaration.description }}</p>
                </div>
                <div v-if="declaration.numeroPlainte" class="plainte-info">
                  <i class="fas fa-file-alt"></i>
                  <span>N° Plainte: {{ declaration.numeroPlainte }}</span>
                </div>
              </div>
              
              <div class="card-actions">
                <button @click="validerDeclarationPerte(declaration._id, 'valider')" class="btn-valider">
                  <i class="fas fa-check"></i>
                  Valider
                </button>
                <button @click="validerDeclarationPerte(declaration._id, 'rejeter')" class="btn-rejeter">
                  <i class="fas fa-times"></i>
                  Rejeter
                </button>
              </div>
            </div>
          </div>
          
          <div v-if="declarationsPertes.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h3>Aucune déclaration de perte en attente</h3>
            <p>Toutes les déclarations ont été traitées</p>
          </div>
        </div>
      </div>
    </div>



    <!-- Contenu Contrôle Créances -->
    <div v-if="activeTab === 'controleCreances'" class="tab-content">
      <ControleCreancesManager :userRole="userRole" />
    </div>

    <!-- Contenu Espace Recouvrement Créances -->
    <div v-if="activeTab === 'recouvrementCreances'" class="tab-content">
      <RecouvrementCreancesManager :userRole="userRole" />
    </div>

    <!-- Contenu Statistiques -->
    <div v-if="activeTab === 'statistiques'" class="tab-content">
      <StatistiquesEncaissements 
        :userRole="userRole"
        :userId="userId"
      />
    </div>

    <!-- Modals -->
    <EncaissementDetailModal 
      v-if="showDetailModal"
      :encaissementId="selectedEncaissementId"
      @close="closeDetailModal"
    />
    
    <RemisePhysiqueModal 
      v-if="showRemiseModal"
      :caisse="caisseJour"
      @close="showRemiseModal = false"
      @reddition-completed="handleRedditionCompleted"
    />
    
    <ControleFinanceModal 
      v-if="showControleModal"
      :caisse="selectedCaisse"
      @close="showControleModal = false"
      @validation-completed="handleValidationCompleted"
    />
    
    <!-- Modal Déclaration de Perte -->
    <DeclarationPerteModal
      :show="showDeclarationPerteModal"
      :userRole="userRole"
      @close="showDeclarationPerteModal = false"
      @declaration-submitted="handleDeclarationPerteSubmitted"
    />

    <!-- Modal Commentaires Validation -->
    <div v-if="showValidationCommentModal" class="modal-overlay" @click="closeValidationCommentModal">
      <div class="modal-content validation-comment-modal" @click.stop>
        <div class="modal-header" :class="validationAction === 'Validée' ? 'header-success' : 'header-danger'">
          <div class="header-icon">
            <i :class="validationAction === 'Validée' ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
          </div>
          <div class="header-text">
            <h4>{{ validationAction === 'Validée' ? '✅ Validation de l\'encaissement' : '❌ Rejet de l\'encaissement' }}</h4>
            <p>{{ validationAction === 'Validée' ? 'Confirmez la validation avec un commentaire' : 'Indiquez le motif du rejet' }}</p>
          </div>
          <button @click="closeValidationCommentModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Résumé de l'encaissement -->
          <div class="encaissement-summary" v-if="selectedEncaissementForValidation">
            <div class="summary-row">
              <span class="label"><i class="fas fa-user"></i> Client</span>
              <span class="value">{{ selectedEncaissementForValidation.prospect?.client?.nomComplet || 'Client' }}</span>
            </div>
            <div class="summary-row">
              <span class="label"><i class="fas fa-money-bill-wave"></i> Montant</span>
              <span class="value montant">{{ formatCurrency(selectedEncaissementForValidation.encaissement?.montant) }}</span>
            </div>
            <div class="summary-row">
              <span class="label"><i class="fas fa-credit-card"></i> Mode</span>
              <span class="value">{{ getModePaiementLabel(selectedEncaissementForValidation.encaissement?.modePaiement) }}</span>
            </div>
            <div class="summary-row">
              <span class="label"><i class="fas fa-user-cog"></i> Technicien</span>
              <span class="value">{{ selectedEncaissementForValidation.technicien?.prenom }} {{ selectedEncaissementForValidation.technicien?.nom }}</span>
            </div>
          </div>

          <!-- Champ commentaire -->
          <div class="form-group">
            <label for="validationComment">
              <i :class="validationAction === 'Validée' ? 'fas fa-comment-dots' : 'fas fa-exclamation-triangle'"></i>
              {{ validationAction === 'Validée' ? 'Commentaires (optionnel)' : 'Motif du rejet *' }}
            </label>
            <textarea 
              id="validationComment"
              v-model="validationComment"
              :placeholder="validationAction === 'Validée' ? 'Ajoutez un commentaire pour cette validation...' : 'Veuillez préciser le motif du rejet...'"
              rows="4"
              class="form-control"
              :class="{ 'is-invalid': validationAction !== 'Validée' && !validationComment.trim() && validationAttempted }"
            ></textarea>
            <small v-if="validationAction !== 'Validée'" class="form-hint">
              <i class="fas fa-info-circle"></i> Le motif est obligatoire pour un rejet
            </small>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeValidationCommentModal" class="btn btn-secondary">
            <i class="fas fa-arrow-left"></i>
            Annuler
          </button>
          <button 
            @click="confirmValidationWithComment" 
            :class="['btn', validationAction === 'Validée' ? 'btn-success' : 'btn-danger']"
            :disabled="validationProcessing || (validationAction !== 'Validée' && !validationComment.trim())"
          >
            <i v-if="!validationProcessing" :class="validationAction === 'Validée' ? 'fas fa-check' : 'fas fa-times'"></i>
            <i v-else class="fas fa-spinner fa-spin"></i>
            {{ validationProcessing ? 'Traitement...' : (validationAction === 'Validée' ? 'Confirmer la validation' : 'Confirmer le rejet') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toastification'
import EncaissementDetailModal from './EncaissementDetailModal.vue'
import StatistiquesEncaissements from './StatistiquesEncaissements.vue'
import RemisePhysiqueModal from './RemisePhysiqueModal.vue'
import ControleFinanceModal from './ControleFinanceModal.vue'
import DepensesManager from './DepensesManager.vue'
import DeclarationPerteModal from './DeclarationPerteModal.vue'
import ControleCreancesManager from './ControleCreancesManager.vue'
import RecouvrementCreancesManager from './RecouvrementCreancesManager.vue'


export default {
  name: 'CaissierManager',
  components: {
    EncaissementDetailModal,
    StatistiquesEncaissements,
    RemisePhysiqueModal,
    ControleFinanceModal,
    DepensesManager,
    DeclarationPerteModal,
    ControleCreancesManager,
    RecouvrementCreancesManager
  },
  props: {
    userRole: String,
    userId: String
  },
  setup(props) {
    const toast = useToast()
    const loading = ref(false)
    const encaissements = ref([])
    const showDetailModal = ref(false)
    const selectedEncaissementId = ref(null)
    const activeTab = ref('encaissements')
    
    // Nouveaux états pour les fonctionnalités financières
    const showRemiseModal = ref(false)
    const showControleModal = ref(false)
    const showDeclarationPerteModal = ref(false)
    const selectedCaisse = ref(null)
    const caisseJour = ref(null)
    const caissesEnValidation = ref([])
    const caissesEnAttente = computed(() => caissesEnValidation.value.length)
    const declarationsPertes = ref([])
    const pendingDeclarationsCount = ref(0)
    const pertesValidees = ref([]) // Pertes validées pour soustraire du total
    const periodeTotale = ref('tout') // Période pour le total: jour, semaine, mois, annee, tout
    
    // États pour les encaissements soumis
    const encaissementsSoumis = ref([])
    const controleSubTab = ref('encaissements') // 'encaissements' ou 'caisses'
    
    // États pour le modal de validation avec commentaire
    const showValidationCommentModal = ref(false)
    const selectedEncaissementForValidation = ref(null)
    const validationAction = ref('Validée')
    const validationComment = ref('')
    const validationProcessing = ref(false)
    const validationAttempted = ref(false)
    
    const filters = ref({
      dateDebut: '',
      dateFin: '',
      technicien: ''
    })
    
    // Liste des techniciens pour le filtre
    const techniciens = ref([])

    const totalEncaissements = computed(() => {
      // Calculer uniquement le total des encaissements avec statut "Validée"
      const totalValide = encaissements.value
        .filter(enc => {
          const statut = enc.statutEncaissement || enc.encaissement?.statut
          return statut === 'Validée'
        })
        .reduce((total, enc) => total + (enc.montant || 0), 0)
      
      // Soustraire les pertes validées
      const totalPertes = pertesValidees.value.reduce((total, perte) => total + (perte.montant || 0), 0)
      
      return totalValide - totalPertes
    })

    // Fonction pour vérifier si une date est dans la période sélectionnée
    const isInPeriode = (dateStr, periode) => {
      if (periode === 'tout') return true
      
      const date = new Date(dateStr)
      const now = new Date()
      
      if (periode === 'jour') {
        return date.toDateString() === now.toDateString()
      } else if (periode === 'semaine') {
        const startOfWeek = new Date(now)
        startOfWeek.setDate(now.getDate() - now.getDay())
        startOfWeek.setHours(0, 0, 0, 0)
        const endOfWeek = new Date(startOfWeek)
        endOfWeek.setDate(startOfWeek.getDate() + 7)
        return date >= startOfWeek && date < endOfWeek
      } else if (periode === 'mois') {
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()
      } else if (periode === 'annee') {
        return date.getFullYear() === now.getFullYear()
      }
      return true
    }

    // Total encaissements filtré par période
    const totalEncaissementsPeriode = computed(() => {
      const totalValide = encaissements.value
        .filter(enc => {
          const statut = enc.statutEncaissement || enc.encaissement?.statut
          const dateEnc = enc.dateEncaissement || enc.rdv?.dateRdv
          return statut === 'Validée' && isInPeriode(dateEnc, periodeTotale.value)
        })
        .reduce((total, enc) => total + (enc.montant || 0), 0)
      
      // Soustraire les pertes validées de la même période
      const totalPertes = pertesValidees.value
        .filter(perte => isInPeriode(perte.dateIncident || perte.dateDeclaration, periodeTotale.value))
        .reduce((total, perte) => total + (perte.montant || 0), 0)
      
      return totalValide - totalPertes
    })

    // Nombre de transactions pour la période
    const transactionsPeriode = computed(() => {
      return encaissements.value.filter(enc => {
        const statut = enc.statutEncaissement || enc.encaissement?.statut
        const dateEnc = enc.dateEncaissement || enc.rdv?.dateRdv
        return statut === 'Validée' && isInPeriode(dateEnc, periodeTotale.value)
      }).length
    })

    const loadEncaissements = async () => {
      loading.value = true
      try {
        const token = localStorage.getItem('token')
        const params = new URLSearchParams()
        
        if (filters.value.dateDebut) params.append('dateDebut', filters.value.dateDebut)
        if (filters.value.dateFin) params.append('dateFin', filters.value.dateFin)
        if (filters.value.technicien) params.append('technicien', filters.value.technicien)
        
        // Pour Financier/Admin : récupérer tous les encaissements
        // Pour Technicien : seulement ses encaissements
        let endpoint = '/api/caissier/encaissements'
        if (props.userRole === 'Financier' || props.userRole === 'Admin') {
          endpoint = '/api/finance/encaissements/tous' // Nouvel endpoint pour tous les encaissements
        }
        
        const response = await fetch(`${endpoint}?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          encaissements.value = data.encaissements || []
        } else {
          // Erreur silencieuse - ne pas afficher de toast pour ne pas perturber l'utilisateur
          console.warn('Erreur lors du chargement des encaissements:', response.status)
          encaissements.value = []
        }
      } catch (error) {
        // Erreur silencieuse - ne pas afficher de toast pour ne pas perturber l'utilisateur
        console.error('Erreur:', error)
        encaissements.value = []
      } finally {
        loading.value = false
      }
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(amount || 0)
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('fr-FR')
    }

    const getModePaiementLabel = (mode) => {
      const modes = {
        'especes': '💵 Espèces',
        'Espèces': '� Eèspèces',
        'cheque': '📝 Chèque',
        'Chèque': '📝 Chèque',
        'carte_bancaire': '💳 Carte Bancaire',
        'CB': '💳 Carte Bancaire',
        'virement': '🏦 Virement',
        'Virement': '🏦 Virement',
        'prelevement': '📋 Prélèvement',
        'Prélèvement': '📋 Prélèvement'
      }
      return modes[mode] || mode
    }

    const getStatutLabel = (statut) => {
      const labels = {
        'Soumise': '⏳ En attente',
        'Validée': '✅ Validé',
        'Rejetée': '❌ Rejeté'
      }
      return labels[statut] || statut || '⏳ En attente'
    }

    const getStatutClass = (statut) => {
      const classes = {
        'Soumise': 'soumise',
        'Validée': 'validee',
        'Rejetée': 'rejetee'
      }
      return classes[statut] || 'soumise'
    }

    const getMissionTypeClass = (type) => {
      const classes = {
        'Installation': 'type-installation',
        'Intervention': 'type-intervention',
        'Recouvrement': 'type-recouvrement'
      }
      return classes[type] || 'type-default'
    }

    const viewDetails = (encaissement) => {
      selectedEncaissementId.value = encaissement._id
      showDetailModal.value = true
    }

    const closeDetailModal = () => {
      showDetailModal.value = false
      selectedEncaissementId.value = null
    }

    const viewPhoto = (encaissement) => {
      // Ouvrir directement le modal de détails qui contient la photo
      viewDetails(encaissement)
    }

    // Nouvelles fonctions pour la gestion financière
    const loadCaisseJour = async () => {
      if (props.userRole !== 'Technicien') {
        console.log('Pas un technicien, pas de chargement de caisse')
        return
      }
      
      try {
        const token = localStorage.getItem('token')
        const today = new Date().toISOString().split('T')[0]
        
        console.log('=== CHARGEMENT CAISSE JOUR ===')
        console.log('Date:', today)
        
        // D'abord essayer de récupérer la caisse existante
        let response = await fetch(`/api/finance/caisse/jour?date=${today}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        console.log('Réponse récupération caisse:', response.status)
        
        if (response.ok) {
          const data = await response.json()
          console.log('Données caisse reçues:', data)
          if (data.caisse) {
            caisseJour.value = data.caisse
            console.log('Caisse chargée:', caisseJour.value)
            return
          }
        }
        
        console.log('Aucune caisse trouvée, création d\'une nouvelle...')
        
        // Si pas de caisse, en créer une nouvelle
        response = await fetch(`/api/finance/caisse/ouvrir`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ date: today })
        })
        
        console.log('Réponse création caisse:', response.status)
        
        if (response.ok) {
          const data = await response.json()
          console.log('Nouvelle caisse créée:', data)
          caisseJour.value = data.caisse
        } else {
          const error = await response.json()
          console.error('Erreur création caisse:', error)
        }
      } catch (error) {
        console.error('Erreur chargement caisse:', error)
      }
    }

    const loadCaissesEnValidation = async () => {
      if (props.userRole !== 'Financier' && props.userRole !== 'Admin') return
      
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/finance/caisses?statut=Remise', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          caissesEnValidation.value = data.caisses || []
        }
      } catch (error) {
        console.error('Erreur chargement caisses:', error)
      }
    }

    const loadEncaissementsSoumis = async () => {
      if (props.userRole !== 'Financier' && props.userRole !== 'Admin') return
      
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/finance/encaissements/soumis', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          encaissementsSoumis.value = data.encaissements || []
          console.log('📋 Encaissements soumis chargés:', encaissementsSoumis.value.length)
        }
      } catch (error) {
        console.error('Erreur chargement encaissements soumis:', error)
      }
    }

    const validerEncaissement = async (encaissementId, statut) => {
      // Trouver l'encaissement dans la liste
      const encaissement = encaissementsSoumis.value.find(e => e._id === encaissementId)
      if (encaissement) {
        selectedEncaissementForValidation.value = encaissement
        validationAction.value = statut
        validationComment.value = ''
        validationAttempted.value = false
        showValidationCommentModal.value = true
      }
    }

    const rejeterEncaissement = async (encaissementId) => {
      // Trouver l'encaissement dans la liste
      const encaissement = encaissementsSoumis.value.find(e => e._id === encaissementId)
      if (encaissement) {
        selectedEncaissementForValidation.value = encaissement
        validationAction.value = 'Rejetée'
        validationComment.value = ''
        validationAttempted.value = false
        showValidationCommentModal.value = true
      }
    }
    
    const closeValidationCommentModal = () => {
      showValidationCommentModal.value = false
      selectedEncaissementForValidation.value = null
      validationComment.value = ''
      validationAttempted.value = false
    }
    
    const confirmValidationWithComment = async () => {
      validationAttempted.value = true
      
      // Vérifier que le motif est fourni pour un rejet
      if (validationAction.value !== 'Validée' && !validationComment.value.trim()) {
        toast.warning('Veuillez fournir un motif de rejet')
        return
      }
      
      validationProcessing.value = true
      
      try {
        const token = localStorage.getItem('token')
        const encaissementId = selectedEncaissementForValidation.value._id
        
        const body = {
          statut: validationAction.value
        }
        
        if (validationAction.value === 'Validée') {
          body.commentairesFinancier = validationComment.value
        } else {
          body.motifRejet = validationComment.value
        }
        
        const response = await fetch(`/api/finance/encaissements/${encaissementId}/valider`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(body)
        })
        
        if (response.ok) {
          const updatedEncaissement = await response.json()
          
          // ===== MISE À JOUR OPTIMISTE IMMÉDIATE =====
          // Mettre à jour l'encaissement dans la liste locale AVANT de recharger
          const index = encaissementsSoumis.value.findIndex(e => e._id === encaissementId)
          if (index !== -1) {
            // Mettre à jour le statut immédiatement dans l'interface
            encaissementsSoumis.value[index] = {
              ...encaissementsSoumis.value[index],
              statut: validationAction.value,
              commentairesFinancier: validationAction.value === 'Validée' ? validationComment.value : encaissementsSoumis.value[index].commentairesFinancier,
              motifRejet: validationAction.value !== 'Validée' ? validationComment.value : null
            }
          }
          // ===== FIN MISE À JOUR OPTIMISTE =====
          
          toast.success(`${validationAction.value === 'Validée' ? '✅ Encaissement validé' : '❌ Encaissement rejeté'} avec succès`)
          closeValidationCommentModal()
          
          // Recharger la liste en arrière-plan pour synchroniser
          setTimeout(() => {
            loadEncaissementsSoumis()
          }, 100)
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la validation')
        }
      } catch (error) {
        console.error('Erreur validation encaissement:', error)
        toast.error('Erreur lors de la validation')
      } finally {
        validationProcessing.value = false
      }
    }

    const handleRedditionCompleted = (caisseUpdated) => {
      caisseJour.value = caisseUpdated
      toast.success('✅ Créance clienteffectuée')
    }

    const handleValidationCompleted = (caisseUpdated) => {
      // Recharger la liste des caisses en validation
      loadCaissesEnValidation()
      toast.success('✅ Caisse validée par le service financier')
    }
    
    const handleDeclarationPerteSubmitted = (result) => {
      showDeclarationPerteModal.value = false
      // Recharger les données si nécessaire
      loadEncaissements()
      loadDeclarationsPertes()
    }
    
    // Charger les déclarations de pertes en attente
    const loadDeclarationsPertes = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/caissier/declarations-pertes?statut=en_attente', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.ok) {
          const data = await response.json()
          declarationsPertes.value = data.data || []
          pendingDeclarationsCount.value = declarationsPertes.value.length
        }
      } catch (error) {
        console.error('Erreur chargement déclarations pertes:', error)
      }
    }
    
    // Charger les pertes validées pour les soustraire du total
    const loadPertesValidees = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/caissier/declarations-pertes?statut=validee', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.ok) {
          const data = await response.json()
          pertesValidees.value = data.data || []
        }
      } catch (error) {
        console.error('Erreur chargement pertes validées:', error)
      }
    }
    
    // Valider ou rejeter une déclaration de perte
    const validerDeclarationPerte = async (declarationId, action) => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/caissier/declaration-perte/${declarationId}/valider`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ action })
        })
        
        if (response.ok) {
          toast.success(`✅ Déclaration ${action === 'valider' ? 'validée' : 'rejetée'} avec succès`)
          loadDeclarationsPertes()
          loadPertesValidees() // Recharger les pertes validées pour mettre à jour le total
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la validation')
        }
      } catch (error) {
        console.error('Erreur validation déclaration:', error)
        toast.error('Erreur lors de la validation')
      }
    }
    
    // Helpers pour les types de pertes
    const getPerteTypeIcon = (type) => {
      const icons = {
        'vol': 'fas fa-user-secret',
        'erreur': 'fas fa-calculator',
        'autre': 'fas fa-question-circle'
      }
      return icons[type] || 'fas fa-exclamation-triangle'
    }
    
    const getPerteTypeLabel = (type) => {
      const labels = {
        'vol': 'Vol',
        'erreur': 'Erreur de caisse',
        'autre': 'Autre'
      }
      return labels[type] || type
    }

    const openControleModal = (caisse) => {
      selectedCaisse.value = caisse
      showControleModal.value = true
    }

    const getCaisseStatusClass = (statut) => {
      const classes = {
        'Ouverte': 'status-open',
        'Remise': 'status-pending',
        'Vérifiée': 'status-verified',
        'Terminée': 'status-closed'
      }
      return classes[statut] || ''
    }

    // Charger la liste des techniciens pour le filtre
    const loadTechniciens = async () => {
      if (props.userRole !== 'Admin' && props.userRole !== 'Financier') return
      
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/users?role=Technicien&limit=100', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          techniciens.value = data.utilisateurs || data.users || []
          console.log('👨‍🔧 Techniciens chargés:', techniciens.value.length)
        }
      } catch (error) {
        console.error('Erreur chargement techniciens:', error)
      }
    }

    onMounted(() => {
      loadEncaissements()
      loadCaisseJour()
      loadCaissesEnValidation()
      loadEncaissementsSoumis()
      loadDeclarationsPertes()
      loadPertesValidees()
      loadTechniciens()
    })

    return {
      loading,
      encaissements,
      filters,
      techniciens,
      totalEncaissements,
      periodeTotale,
      totalEncaissementsPeriode,
      transactionsPeriode,
      showDetailModal,
      selectedEncaissementId,
      activeTab,
      showRemiseModal,
      showControleModal,
      selectedCaisse,
      caisseJour,
      caissesEnValidation,
      caissesEnAttente,
      encaissementsSoumis,
      controleSubTab,
      loadEncaissements,
      loadTechniciens,
      handleRedditionCompleted,
      handleValidationCompleted,
      openControleModal,
      getCaisseStatusClass,
      validerEncaissement,
      rejeterEncaissement,
      showValidationCommentModal,
      selectedEncaissementForValidation,
      validationAction,
      validationComment,
      validationProcessing,
      validationAttempted,
      closeValidationCommentModal,
      confirmValidationWithComment,
      formatCurrency,
      formatDate,
      getModePaiementLabel,
      getStatutLabel,
      getStatutClass,
      getMissionTypeClass,
      viewDetails,
      closeDetailModal,
      viewPhoto,
      showDeclarationPerteModal,
      declarationsPertes,
      pendingDeclarationsCount,
      handleDeclarationPerteSubmitted,
      loadDeclarationsPertes,
      validerDeclarationPerte,
      getPerteTypeIcon,
      getPerteTypeLabel
    }
  }
}
</script>

<style scoped>
.caissier-manager {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
  position: relative;
  overflow: hidden;
}

.header-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-size: 1.8rem;
}

.header-text h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: 700;
}

.header-text p {
  margin: 0;
  opacity: 0.9;
}

.header-stats {
  display: flex;
  gap: 2rem;
}

.stat-card {
  text-align: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1rem 1.5rem;
}

.stat-card.total-card {
  min-width: 160px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.85rem;
  opacity: 0.9;
}

.periode-select {
  margin-top: 0.5rem;
  padding: 0.35rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.75rem;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
}

.periode-select:hover {
  background: rgba(255, 255, 255, 0.3);
}

.periode-select:focus {
  border-color: rgba(255, 255, 255, 0.5);
}

.periode-select option {
  background: #001bb7;
  color: white;
}

/* Onglets */
.tabs-section {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid rgba(0, 27, 183, 0.1);
}

.tab-btn {
  padding: 1rem 2rem;
  border: none;
  background: transparent;
  color: #6c757d;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  position: relative;
}

.tab-btn:hover {
  color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.05);
}

.tab-btn.active {
  color: var(--primary-blue);
  border-bottom-color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.05);
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 2px 2px 0 0;
}

/* Onglets spéciaux créances */
.tab-btn.creances-tab.active {
  color: #f59e0b;
  border-bottom-color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
}

.tab-btn.creances-tab.active::after {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.tab-btn.recouvrement-creances-tab.active {
  color: #10b981;
  border-bottom-color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.tab-btn.recouvrement-creances-tab.active::after {
  background: linear-gradient(135deg, #10b981, #059669);
}

/* Contenu des onglets */
.tab-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Filtres */
.filters-section {
  background: rgba(248, 250, 252, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.filter-group {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.filter-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border-radius: 12px;
  border: 2px solid rgba(0, 27, 183, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.filter-wrapper:focus-within {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1);
}

.filter-icon-container {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.9rem;
}

.filter-input {
  border: none;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  background: transparent;
  outline: none;
  min-width: 160px;
}

.filter-select {
  border: none;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  background: transparent;
  outline: none;
  min-width: 200px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;
}

.filter-technicien {
  min-width: 250px;
}

.filter-technicien .filter-select {
  min-width: 180px;
}

.btn-refresh {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-refresh:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.3);
}

/* Grid des encaissements */
.encaissements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.encaissement-card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.encaissement-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 27, 183, 0.15);
}

.card-header {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.03));
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.client-info h4 {
  margin: 0 0 0.25rem 0;
  color: var(--primary-blue);
  font-weight: 600;
}

.client-info p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.technicien-info {
  color: var(--primary-blue) !important;
  font-weight: 600 !important;
  margin-top: 0.5rem !important;
}

.technicien-info i {
  margin-right: 0.5rem;
}

.montant {
  font-size: 1.5rem;
  font-weight: 700;
  color: #10b981;
}

.card-body {
  padding: 1.5rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.info-row:last-child {
  margin-bottom: 0;
}

.label {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.9rem;
}

.value {
  font-weight: 500;
  color: #1a202c;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
}

.card-actions {
  padding: 1rem 1.5rem;
  background: rgba(248, 250, 252, 0.5);
  display: flex;
  gap: 0.75rem;
}

.btn-details, .btn-photo {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.btn-details {
  background: var(--primary-blue);
  color: white;
}

.btn-photo {
  background: #10b981;
  color: white;
}

.btn-details:hover, .btn-photo:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* États vides et loading */
.empty-state, .loading-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 27, 183, 0.1);
  border-left: 4px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Nouvelles sections */
.reddition-section,
.controle-section {
  padding: 1rem 0;
}

.reddition-header,
.controle-header {
  text-align: center;
  margin-bottom: 2rem;
}

.reddition-header h3,
.controle-header h3 {
  margin: 0 0 0.5rem 0;
  color: var(--primary-blue);
  font-size: 1.5rem;
  font-weight: 700;
}

.reddition-header p,
.controle-header p {
  margin: 0;
  color: #6c757d;
}

/* Caisse info */
.caisse-info {
  max-width: 600px;
  margin: 0 auto;
}

.caisse-card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.caisse-status {
  padding: 1rem;
  text-align: center;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-open {
  background: #fef3c7;
  color: #92400e;
}

.status-pending {
  background: #fed7aa;
  color: #ea580c;
}

.status-verified {
  background: #d1fae5;
  color: #065f46;
}

.status-closed {
  background: #e0e7ff;
  color: #3730a3;
}

.caisse-details {
  padding: 1.5rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-row span:first-child {
  font-weight: 600;
  color: #6c757d;
}

.detail-row span:last-child {
  font-weight: 500;
  color: #1a202c;
}

.caisse-actions {
  padding: 1.5rem;
  background: rgba(248, 250, 252, 0.5);
  text-align: center;
}

.btn-reddition,
.btn-debug {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-debug {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  font-size: 0.9rem;
}

.btn-reddition:hover,
.btn-debug:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.3);
}

.status-message {
  color: #10b981;
  font-weight: 600;
}

.no-caisse {
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
}

/* Caisses en validation */
.caisses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.caisse-validation-card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.caisse-validation-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 27, 183, 0.15);
}

.technicien-info h4 {
  margin: 0 0 0.25rem 0;
  color: var(--primary-blue);
  font-weight: 600;
}

.technicien-info p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.statut-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.montants-summary {
  padding: 1.5rem;
}

.montant-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.montant-item:last-child {
  margin-bottom: 0;
}

.montant-item .label {
  font-weight: 600;
  color: #6c757d;
  min-width: 80px;
}

.montant-item .value {
  font-weight: 700;
  color: #1a202c;
  margin-left: 1rem;
}

.montant-item .ecart {
  margin-left: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.montant-item .ecart.surplus {
  color: #10b981;
}

.montant-item .ecart.deficit {
  color: #ef4444;
}

.btn-controle {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: center;
}

.btn-controle:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.3);
}

/* Badge pour les onglets */
.badge {
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  margin-left: 0.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }
  
  .header-stats {
    justify-content: center;
  }
  
  .filter-group {
    flex-direction: column;
    align-items: stretch;
  }
  
  .encaissements-grid,
  .caisses-grid {
    grid-template-columns: 1fr;
  }
  
  .tabs-section {
    overflow-x: auto;
    white-space: nowrap;
  }
  
  .tab-btn {
    flex-shrink: 0;
  }
}

/* Sous-onglets pour Contrôle Financier */
.sub-tabs {
  display: flex;
  gap: 1rem;
  margin: 1.5rem 0;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 12px;
}

.sub-tab-btn {
  flex: 1;
  padding: 0.75rem 1.5rem;
  background: white;
  border: 2px solid transparent;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #64748b;
}

.sub-tab-btn:hover {
  background: #f1f5f9;
  border-color: #e2e8f0;
}

.sub-tab-btn.active {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-color: var(--primary-blue);
}

.sub-tab-btn .badge {
  background: rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}

.sub-tab-btn:not(.active) .badge {
  background: #ef4444;
  color: white;
}

/* Cartes d'encaissement en validation */
.encaissement-validation-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.encaissement-validation-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  border-color: var(--primary-blue);
  transform: translateY(-2px);
}

.encaissement-validation-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 2px solid #e2e8f0;
}

.encaissement-validation-card .client-info h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  color: #1e293b;
}

.encaissement-validation-card .client-info p {
  margin: 0.25rem 0;
  font-size: 0.85rem;
  color: #64748b;
}

.encaissement-validation-card .technicien-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #3b82f6;
  font-weight: 500;
}

.encaissement-validation-card .montant {
  font-size: 1.5rem;
  font-weight: 700;
  color: #10b981;
}

.encaissement-validation-card .card-body {
  padding: 1.5rem;
}

.encaissement-validation-card .info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.encaissement-validation-card .info-row:last-child {
  border-bottom: none;
}

.encaissement-validation-card .label {
  font-weight: 600;
  color: #64748b;
}

.encaissement-validation-card .value {
  font-weight: 500;
  color: #1e293b;
}

.statut-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  display: inline-block;
}

.statut-badge.soumise {
  background: #fef3c7;
  color: #92400e;
}

.statut-badge.validee {
  background: #d1fae5;
  color: #065f46;
}

.statut-badge.rejetee {
  background: #fee2e2;
  color: #991b1b;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.85rem;
}

.encaissement-validation-card .card-actions {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-top: 2px solid #e2e8f0;
}

.btn-valider,
.btn-rejeter {
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-valider {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-valider:hover {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.btn-rejeter {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.btn-rejeter:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

/* ========== RESPONSIVE MOBILE ========== */
@media (max-width: 768px) {
  .caissier-manager {
    padding: 0.5rem;
  }
  
  /* Header responsive */
  .header-section {
    padding: 1rem;
    border-radius: 12px;
    margin-bottom: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .header-info {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }
  
  .icon-wrapper {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
    margin: 0 auto;
  }
  
  .header-text h2 {
    font-size: 1.4rem;
  }
  
  .header-text p {
    font-size: 0.85rem;
  }
  
  .header-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    width: 100%;
  }
  
  .stat-card {
    padding: 0.75rem;
  }
  
  .stat-value {
    font-size: 1.25rem;
  }
  
  .stat-label {
    font-size: 0.75rem;
  }
  
  /* Onglets responsive */
  .tabs-section {
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.5rem;
    background: #f8fafc;
    border-radius: 12px;
    border-bottom: none;
    margin-bottom: 1rem;
  }
  
  .tab-btn {
    flex: 1;
    min-width: calc(50% - 0.25rem);
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 8px;
    border-bottom: none;
    justify-content: center;
    text-align: center;
  }
  
  .tab-btn span {
    display: none;
  }
  
  .tab-btn i {
    font-size: 1rem;
  }
  
  .tab-btn.active {
    background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
    color: white;
  }
  
  .tab-btn .badge {
    position: absolute;
    top: -5px;
    right: -5px;
    font-size: 0.65rem;
    padding: 0.15rem 0.35rem;
  }
  
  /* Debug info - cacher sur mobile */
  .debug-info {
    display: none;
  }
  
  /* Filtres responsive */
  .filters-section {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
  
  .filter-group {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .filter-wrapper {
    width: 100%;
  }
  
  .btn-refresh {
    width: 100%;
    justify-content: center;
  }
  
  /* Grille des encaissements responsive */
  .encaissements-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .encaissement-card {
    border-radius: 12px;
  }
  
  .encaissement-card .card-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
    padding: 1rem;
  }
  
  .encaissement-card .montant {
    font-size: 1.25rem;
    align-self: flex-end;
  }
  
  .encaissement-card .card-body {
    padding: 1rem;
  }
  
  .encaissement-card .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    padding: 0.5rem 0;
  }
  
  .encaissement-card .card-actions {
    flex-direction: column;
    padding: 1rem;
    gap: 0.5rem;
  }
  
  .btn-details,
  .btn-photo {
    width: 100%;
    justify-content: center;
  }
  
  /* Reddition section responsive */
  .reddition-section {
    padding: 1rem;
  }
  
  .reddition-header h3 {
    font-size: 1.2rem;
  }
  
  .caisse-card {
    padding: 1rem;
  }
  
  .caisse-details .detail-row {
    flex-direction: column;
    gap: 0.25rem;
    align-items: flex-start;
  }
  
  .btn-reddition {
    width: 100%;
    justify-content: center;
  }
  
  /* Contrôle section responsive */
  .controle-section {
    padding: 0.5rem;
  }
  
  .controle-header h3 {
    font-size: 1.2rem;
  }
  
  .sub-tabs {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .sub-tab-btn {
    width: 100%;
    justify-content: center;
  }
  
  /* Cartes de validation responsive */
  .encaissement-validation-card .card-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }
  
  .encaissement-validation-card .montant {
    font-size: 1.25rem;
  }
  
  .encaissement-validation-card .card-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .btn-valider,
  .btn-rejeter {
    width: 100%;
  }
  
  /* Caisses grid responsive */
  .caisses-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .caisse-validation-card .card-header {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .montants-summary {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .montant-item {
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  
  .btn-controle {
    width: 100%;
    justify-content: center;
  }
  
  /* Empty state responsive */
  .empty-state {
    padding: 2rem 1rem;
  }
  
  .empty-icon {
    font-size: 2.5rem;
  }
  
  .empty-state h3 {
    font-size: 1.1rem;
  }
  
  .empty-state p {
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .caissier-manager {
    padding: 0.25rem;
  }
  
  .header-section {
    padding: 0.75rem;
  }
  
  .header-text h2 {
    font-size: 1.2rem;
  }
  
  .stat-value {
    font-size: 1.1rem;
  }
  
  .tabs-section {
    padding: 0.25rem;
  }
  
  .tab-btn {
    padding: 0.625rem 0.25rem;
    font-size: 0.7rem;
  }
  
  .encaissement-card .card-header,
  .encaissement-card .card-body,
  .encaissement-card .card-actions {
    padding: 0.75rem;
  }
  
  .encaissement-card .montant {
    font-size: 1.1rem;
  }
}

/* Section Déclarations de Pertes */
.declarations-pertes-section {
  padding: 1rem 0;
}

.pertes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.perte-validation-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.perte-validation-card.vol {
  border-left-color: #ef4444;
}

.perte-validation-card.erreur {
  border-left-color: #f59e0b;
}

.perte-validation-card.autre {
  border-left-color: #6b7280;
}

.perte-validation-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.perte-validation-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.25rem;
  background: linear-gradient(135deg, #fef2f2, #fee2e2);
  border-bottom: 1px solid #fecaca;
}

.perte-type {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.type-badge.vol {
  background: #fee2e2;
  color: #991b1b;
}

.type-badge.erreur {
  background: #fef3c7;
  color: #92400e;
}

.type-badge.autre {
  background: #f3f4f6;
  color: #4b5563;
}

.perte-type .date {
  font-size: 0.8rem;
  color: #6b7280;
}

.montant-perte {
  font-size: 1.4rem;
  font-weight: 700;
  color: #ef4444;
}

.perte-validation-card .card-body {
  padding: 1.25rem;
}

.declarant-info,
.date-incident,
.plainte-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #4b5563;
  margin-bottom: 0.5rem;
}

.declarant-info i,
.date-incident i,
.plainte-info i {
  color: #9ca3af;
  width: 16px;
}

.description {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 3px solid #d1d5db;
}

.description p {
  margin: 0;
  font-size: 0.85rem;
  color: #374151;
  line-height: 1.5;
}

.perte-validation-card .card-actions {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
}

/* Badge danger pour les pertes */
.badge.danger {
  background: #ef4444;
}

/* Sous-onglet pertes */
.sub-tab-btn.pertes.active {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

/* Barre d'actions Financier */
.financier-actions-bar {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fecaca;
  border-radius: 16px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
}

.actions-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.actions-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #991b1b;
  font-weight: 600;
  font-size: 0.9rem;
}

.actions-info i {
  font-size: 1.2rem;
}

/* Bouton Déclarer une perte - Design Pro */
.btn-declare-loss-pro {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  border-radius: 14px;
  padding: 0.875rem 1.25rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.btn-declare-loss-pro:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(239, 68, 68, 0.4);
}

.btn-declare-loss-pro .btn-icon {
  width: 42px;
  height: 42px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}

.btn-declare-loss-pro .btn-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.btn-declare-loss-pro .btn-title {
  font-weight: 700;
  font-size: 0.95rem;
}

.btn-declare-loss-pro .btn-subtitle {
  font-size: 0.75rem;
  opacity: 0.85;
  font-weight: 400;
}

.btn-declare-loss-pro .btn-arrow {
  margin-left: auto;
  opacity: 0.7;
  transition: transform 0.3s ease;
}

.btn-declare-loss-pro:hover .btn-arrow {
  transform: translateX(4px);
  opacity: 1;
}

@media (max-width: 768px) {
  .financier-actions-bar {
    padding: 0.875rem 1rem;
  }
  
  .actions-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .actions-info {
    justify-content: center;
    margin-bottom: 0.5rem;
  }
  
  .btn-declare-loss-pro {
    width: 100%;
    justify-content: center;
  }
  
  .btn-declare-loss-pro .btn-subtitle {
    display: none;
  }
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Modal Content - Validation Comment */
.validation-comment-modal {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.validation-comment-modal .modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  position: relative;
}

.validation-comment-modal .modal-header.header-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.validation-comment-modal .modal-header.header-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.validation-comment-modal .header-icon {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.validation-comment-modal .header-text {
  flex: 1;
}

.validation-comment-modal .header-text h4 {
  margin: 0 0 0.25rem 0;
  font-size: 1.15rem;
  font-weight: 700;
}

.validation-comment-modal .header-text p {
  margin: 0;
  font-size: 0.85rem;
  opacity: 0.9;
}

.validation-comment-modal .close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.validation-comment-modal .close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.validation-comment-modal .modal-body {
  padding: 1.5rem;
}

/* Résumé de l'encaissement */
.encaissement-summary {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row .label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.85rem;
}

.summary-row .label i {
  width: 18px;
  color: #94a3b8;
}

.summary-row .value {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
}

.summary-row .value.montant {
  color: #10b981;
  font-size: 1.1rem;
}

/* Form Group */
.validation-comment-modal .form-group {
  margin-bottom: 0;
}

.validation-comment-modal .form-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.validation-comment-modal .form-group label i {
  color: #6b7280;
}

.validation-comment-modal .form-control {
  width: 100%;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.validation-comment-modal .form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.validation-comment-modal .form-control.is-invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

.validation-comment-modal .form-control::placeholder {
  color: #9ca3af;
}

.validation-comment-modal .form-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.validation-comment-modal .form-hint i {
  color: #9ca3af;
}

/* Modal Footer */
.validation-comment-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
}

.validation-comment-modal .btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.validation-comment-modal .btn-secondary {
  background: #f1f5f9;
  color: #475569;
}

.validation-comment-modal .btn-secondary:hover {
  background: #e2e8f0;
}

.validation-comment-modal .btn-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.validation-comment-modal .btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.validation-comment-modal .btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.validation-comment-modal .btn-danger:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.validation-comment-modal .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 576px) {
  .validation-comment-modal {
    max-width: 100%;
    margin: 0.5rem;
    border-radius: 16px;
  }
  
  .validation-comment-modal .modal-header {
    padding: 1.25rem;
  }
  
  .validation-comment-modal .header-icon {
    width: 42px;
    height: 42px;
    font-size: 1.25rem;
  }
  
  .validation-comment-modal .modal-body {
    padding: 1.25rem;
  }
  
  .validation-comment-modal .modal-footer {
    flex-direction: column;
    padding: 1rem;
  }
  
  .validation-comment-modal .btn {
    width: 100%;
    justify-content: center;
  }
}

/* Type de Mission Badge */
.type-mission-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
}

.type-mission-badge.type-installation {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.type-mission-badge.type-intervention {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.type-mission-badge.type-recouvrement {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.type-mission-badge.type-default {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
}
</style>
